import { useEffect, useState, useCallback } from "react";
import { useParams, Link } from "react-router-dom";
import { Loader2, ArrowLeft, Pin, Flag, Shield, UserX } from "lucide-react";
import { toast } from "sonner";
import Navbar from "../components/Navbar";
import api, { formatApiError } from "../lib/api";

const panel = "rounded-lg border border-[var(--border-subtle)] bg-[var(--bg-card)] p-4";
const field = "w-full rounded-md border border-[var(--border-subtle)] bg-[var(--bg-base)] px-3 py-2 text-sm text-[var(--text-primary)] outline-none focus:border-[var(--accent)]";
const btn = "inline-flex items-center justify-center gap-1.5 rounded-md bg-[var(--accent)] px-4 py-2 text-sm font-bold uppercase tracking-wide text-[var(--accent-contrast)] hover:bg-[var(--accent-hover)] disabled:opacity-50";

export default function ThreadView() {
  const { slug, tid } = useParams();
  const [cid, setCid] = useState(null);
  const [thread, setThread] = useState(null);
  const [posts, setPosts] = useState([]);
  const [community, setCommunity] = useState(null);
  const [reply, setReply] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data: fc } = await api.get(`/communities/film/${slug}`);
      const id = fc.community.id; setCid(id);
      const { data } = await api.get(`/communities/${id}/threads/${tid}`);
      setThread(data.thread); setPosts(data.posts); setCommunity(data.community);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Could not load thread"); }
  }, [slug, tid]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!reply.trim()) return;
    setBusy(true);
    try {
      const { data } = await api.post(`/communities/${cid}/threads/${tid}/posts`, { body: reply });
      toast.success(data.pending ? "Reply posted — awaiting review before it goes live." : "Reply posted ★");
      setReply(""); await load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); } finally { setBusy(false); }
  };

  const report = async (target_type, target_id) => {
    const reason = window.prompt("Report this content — tell us why (optional):") ?? null;
    if (reason === null) return;
    try { await api.post("/communities/report", { target_type, target_id, reason }); toast.success("Thanks — reported to the moderators."); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  const ban = async (uid) => {
    if (!window.confirm("Ban this member from the space?")) return;
    try { await api.post(`/communities/${cid}/ban`, { user_id: uid }); toast.success("Member banned"); await load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Failed"); }
  };

  if (!thread) return (
    <div className="min-h-screen bg-[var(--bg-base)]" data-skin="classic-red"><Navbar /><div className="flex items-center justify-center gap-2 p-16 text-[var(--text-secondary)]"><Loader2 className="animate-spin" size={18} /> Loading…</div></div>
  );

  const canReply = community && (community.is_member || community.is_creator) && !community.is_banned;
  const canMod = community && community.can_moderate;

  return (
    <div className="min-h-screen bg-[var(--bg-base)]" data-skin="classic-red" data-testid="thread-view">
      <Navbar />
      <main className="mx-auto max-w-2xl px-4 py-6">
        <Link to={`/community/film/${slug}`} className="inline-flex items-center gap-1 text-sm text-[var(--text-secondary)] hover:text-[var(--accent-hover)]" data-testid="thread-back"><ArrowLeft size={14} /> Back to space</Link>

        <article className="mt-4" data-testid="thread-op">
          <div className="flex items-center gap-2">
            {thread.pinned && <Pin size={14} className="text-[var(--accent-hover)]" />}
            <h1 className="font-head text-2xl text-[var(--text-primary)]">{thread.title}</h1>
          </div>
          <p className="mt-1 text-xs text-[var(--text-muted)]">by {thread.author_name}</p>
          {thread.body && <p className="mt-3 whitespace-pre-wrap text-[var(--text-primary)]">{thread.body}</p>}
        </article>

        <h2 className="mt-7 text-xs font-bold uppercase tracking-wider text-[var(--text-secondary)]">{thread.reply_count} {thread.reply_count === 1 ? "reply" : "replies"}</h2>
        <div className="mt-3 space-y-3">
          {posts.length === 0 ? <p className="text-sm text-[var(--text-muted)]" data-testid="thread-no-posts">No replies yet. Be the first.</p> : posts.map((p) => (
            <div key={p.id} className={panel} data-testid={`post-${p.id}`}>
              <div className="flex items-center justify-between gap-2">
                <p className="text-xs font-bold text-[var(--text-primary)]">{p.author_name} {p.author_id === community.creator_user_id && <span className="ml-1 inline-flex items-center gap-1 text-[var(--accent-hover)]"><Shield size={11} /> Filmmaker</span>}</p>
                <div className="flex items-center gap-2">
                  {p.status === "pending" && <span className="rounded bg-[var(--text-muted)]/20 px-1.5 py-0.5 text-[9px] font-bold uppercase text-[var(--text-secondary)]">Awaiting review</span>}
                  <button onClick={() => report("post", p.id)} className="text-[var(--text-muted)] hover:text-red-500" title="Report" data-testid={`report-post-${p.id}`}><Flag size={13} /></button>
                  {canMod && p.author_id !== community.creator_user_id && <button onClick={() => ban(p.author_id)} className="text-[var(--text-muted)] hover:text-red-500" title="Ban member" data-testid={`ban-post-${p.id}`}><UserX size={13} /></button>}
                </div>
              </div>
              <p className="mt-2 whitespace-pre-wrap text-sm text-[var(--text-primary)]">{p.body}</p>
            </div>
          ))}
        </div>

        {community && community.is_banned ? (
          <p className="mt-6 rounded-md bg-red-500/15 px-3 py-2 text-sm text-red-400" data-testid="thread-banned">You are banned from this space.</p>
        ) : canReply ? (
          <div className="mt-6" data-testid="reply-box">
            <textarea className={`${field} min-h-[90px]`} placeholder="Add a reply…" value={reply} onChange={(e) => setReply(e.target.value)} data-testid="reply-input" />
            <button onClick={submit} disabled={busy || !reply.trim()} className={`${btn} mt-2`} data-testid="reply-submit">{busy ? <Loader2 size={14} className="animate-spin" /> : null} Reply</button>
          </div>
        ) : (
          <p className="mt-6 text-sm text-[var(--text-secondary)]"><Link to={`/community/film/${slug}`} className="font-semibold text-[var(--accent-hover)]">Join the space</Link> to reply.</p>
        )}
      </main>
    </div>
  );
}
